import { HoverCard } from "#components/ui/HoverCard.tsx";
import { TokenWrittenAs } from "#features/conversations/components/conversation-input/TokenWrittenAs.tsx";
import { MentionMemberCard } from "#features/conversations/components/conversation-user/MentionMemberCard.tsx";
import { usePrefetchMemberCard } from "#features/conversations/hooks/useMemberCard.ts";
import { useHoverCard } from "#hooks/useHoverCard.ts";
import { generateDecorativeColorByName } from "@hopper-ui/components";
import { useCallback, useId, useRef, useState, type CSSProperties } from "react";
import { Button } from "react-aria-components";
import "./MentionToken.css";

interface MentionCard {
    // Looks the member up for the rest of what their profile card shows.
    memberId?: string;
    jobTitle?: string | null;
    // Closes the card and keeps it shut, as the composer does while the user types or a tray is open.
    isDisabled?: boolean;
    // A sent message's token is a button that only opens its card, as SkillToken's is: keyboard focus or a tap opens it.
    isPressable?: boolean;
    // The card links the member's wl-app page in a new tab; the token itself only opens the card.
    linksToProfile?: boolean;
}

interface MentionTokenProps {
    name: string;
    // The member's photo, when it's known; the token shows their initial otherwise.
    imageUrl?: string | null;
    // Copies the mention as "@Name", as the message reads in plain text.
    copiesWithAt?: boolean;
    // Who the member is, on hover; without it the token is only its face and name.
    card?: MentionCard;
}

// The letter and colour Hopper's xs Avatar gives the member, as the @ list shows them. The letter is drawn by CSS from
// `data-initial`, so it stays out of the token's text, its copy and its accessible name.
const MemberInitial = ({ name }: { name: string }) => {
    const color = generateDecorativeColorByName(name);
    const style = { "--wai-mention-initial-background": `var(--hop-${color}-surface-strong)`, "--wai-mention-initial-color": `var(--hop-${color}-text)` } as CSSProperties;

    return <span className="wai-mention-token__face-initial" style={style} data-initial={[...name.trim()][0] ?? ""} aria-hidden data-testid="mention-initial" />;
};

// The initial holds the photo's place until the photo has loaded, so a face never shows as an empty circle; a photo
// the browser already has appears without the crossfade, so a token drawn again doesn't fade again.
type PhotoState = "loading" | "fading" | "shown" | "failed";

// The member's photo over their initial, which shows when they have none or it fails to load. Plain inline elements
// rather than Hopper's Avatar, which is a block-level `role="img"` named after the member: the token already reads as
// their name.
const MentionTokenFace = ({ name, imageUrl }: { name: string; imageUrl?: string | null }) => {
    const [photo, setPhoto] = useState<PhotoState>("loading");
    const showIfCached = useCallback((image: HTMLImageElement | null) => {
        if (image?.complete && image.naturalWidth > 0) {
            setPhoto("shown");
        }
    }, []);
    const hasPhoto = Boolean(imageUrl) && photo !== "failed";

    return (
        <span className="wai-mention-token__face" data-photo={hasPhoto ? photo : undefined} data-testid="mention-face">
            <MemberInitial name={name} />
            {hasPhoto &&
                <img
                    ref={showIfCached}
                    className="wai-mention-token__face-photo"
                    src={imageUrl ?? undefined}
                    alt=""
                    aria-hidden
                    draggable={false}
                    data-testid="mention-avatar"
                    onLoad={() => setPhoto(current => (current === "shown" ? current : "fading"))}
                    onError={() => setPhoto("failed")}
                />
            }
        </span>
    );
};

const tokenClassName = (copiesWithAt: boolean) => (copiesWithAt ? "wai-mention-token wai-token--copies-written" : "wai-mention-token");

export const MentionTokenContent = ({ name, imageUrl, copiesWithAt }: Required<Pick<MentionTokenProps, "name" | "copiesWithAt">> & Pick<MentionTokenProps, "imageUrl">) => (
    <>
        {copiesWithAt && <TokenWrittenAs text={`@${name}`} />}
        <MentionTokenFace key={imageUrl} name={name} imageUrl={imageUrl} />
        {name}
    </>
);

const MentionTokenWithCard = ({ name, imageUrl, copiesWithAt = false, memberId, jobTitle, isDisabled = false, isPressable = false, linksToProfile = false }: MentionCard & Omit<MentionTokenProps, "card">) => {
    const spanRef = useRef<HTMLSpanElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const triggerRef = isPressable ? buttonRef : spanRef;
    const cardRef = useRef<HTMLElement>(null);
    const cardId = useId();
    const { isOpen, triggerProps, popoverProps, cardProps } = useHoverCard({ triggerRef, cardRef, isDisabled, opensOnPress: isPressable });
    const prefetchMemberCard = usePrefetchMemberCard();
    const className = tokenClassName(copiesWithAt);
    const content = <MentionTokenContent name={name} imageUrl={imageUrl} copiesWithAt={copiesWithAt} />;

    // Looks the member up when the card is first wanted rather than once it opens, so it opens with them already there.
    const prefetchCard = () => {
        if (memberId && !isDisabled) {
            prefetchMemberCard(memberId);
        }
    };
    const onFocus = () => {
        prefetchCard();
        triggerProps.onFocus();
    };

    return (
        <>
            {isPressable
                ? (
                    <Button ref={buttonRef} className={className} data-private="" aria-describedby={isOpen ? cardId : undefined} {...triggerProps} onPointerEnter={prefetchCard} onFocus={onFocus}>
                        {content}
                    </Button>
                )
                : (
                    // The composer's caret can't land on a token, so it opens on hover alone.
                    <span ref={spanRef} className={className} data-private="" onPointerEnter={prefetchCard} onPointerMove={triggerProps.onPointerMove} onPointerLeave={triggerProps.onPointerLeave}>
                        {content}
                    </span>
                )}
            {/* The card opens outside the token, so it keeps the member, its label included, out of session recordings itself. */}
            <HoverCard triggerRef={triggerRef} cardRef={cardRef} cardId={cardId} label={name} className="wai-mention-hover-card" isPrivate popoverProps={popoverProps} cardProps={cardProps}>
                <MentionMemberCard memberId={memberId} fullName={name} imageUrl={imageUrl} jobTitle={jobTitle} linksToProfile={linksToProfile} />
            </HoverCard>
        </>
    );
};

// A mention as the composer and a sent message both show it. It sits in the main bundle so the transcript can draw it
// without the editor; it takes the colour of the text around it.
export const MentionToken = ({ name, imageUrl, copiesWithAt = false, card }: MentionTokenProps) => (card
    ? <MentionTokenWithCard name={name} imageUrl={imageUrl} copiesWithAt={copiesWithAt} {...card} />
    : (
        <span className={tokenClassName(copiesWithAt)} data-private="">
            <MentionTokenContent name={name} imageUrl={imageUrl} copiesWithAt={copiesWithAt} />
        </span>
    ));
